import { useCallback, useEffect, useMemo, useState } from "react";

import { ViewBooking, ViewBookingType } from "collins-js-sdk";
import { motion } from "framer-motion";

import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { filterBookings } from "../../../../../utils/booking";
import {
  completeBookingsFilter,
  leftBookingsFilter,
  openBookingsFilter,
  waitlistBookingsFilter
} from "../../../../../utils/bookingFilters";
import { ExpandableSidebar } from "../../../../Layout/ExpandableSidebar/ExpandableSidebar";
import { DiaryAddBookingSection } from "../DiaryAddBookingSection/DiaryAddBookingSection";
import { DiarySidebarSections } from "../DiarySidebarSection/DiarySidebarSections";
import { DiarySection } from "../DiaryTabs/DiaryTabs";

import { editBooking } from "../../../../../APIs/Booking/UpdateBooking";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { BookingTypeEnum } from "../../../../../types/bookingEnums";
import { dmnStorage } from "../../../../../utils/dmnStorage";
import { BookingShortcuts } from "../BookingShortcuts/BookingShortcuts";
import { DiarySearch } from "../DiarySearch/DiarySearch";
import { DiaryTabsWrapper } from "../DiaryTabsWrapper/DiaryTabsWrapper";

export const DiarySidebar = () => {
  const [prefillBookingType, setPrefillBookingType] = useState<
    ViewBookingType | null | undefined
  >();

  const diaryContext = useDiaryContext();
  const {
    showSidebar,
    setDiaryContext,
    openedFromEnquiry,
    spotlightRequestedTabKey
  } = diaryContext;
  const appContext = useAppContext();

  const [currentSectionKey, setCurrentSectionKey] = useState("bookings");

  // When Spotlight opens a booking, it sets spotlightRequestedTabKey so the
  // list underneath is filtered to the matching tab. Consume it once then
  // clear so a later user-initiated tab click isn't overridden.
  useEffect(() => {
    if (spotlightRequestedTabKey) {
      setCurrentSectionKey(spotlightRequestedTabKey);
      setDiaryContext({ spotlightRequestedTabKey: undefined });
    }
  }, [spotlightRequestedTabKey, setDiaryContext]);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [showSearchForBooking, setShowSearchForBooking] =
    useState<boolean>(false);

  const toggleAddBooking = (numPeople?: number) => {
    if (!showAddBooking) {
      setDiaryContext({ showSidebar: true });
    }

    if (numPeople) {
      setPrefillBookingType({ numPeople } as any);
    }

    setShowAddBooking(!showAddBooking);
  };

  useEffect(() => {
    if (showSidebar === undefined) {
      (async () => {
        const storedSidebar = (await dmnStorage.get("showSidebar")) ?? true;
        setDiaryContext({ showSidebar: storedSidebar });
      })();
    } else {
      dmnStorage.add("showSidebar", showSidebar);
    }
  }, [showSidebar, setDiaryContext]);

  const gridAreaClick = diaryContext.gridAreaClick;
  useEffect(() => {
    if (gridAreaClick?.type === "double") {
      setDiaryContext({ showSidebar: true });
      setShowAddBooking(true);
    }
  }, [gridAreaClick, setDiaryContext]);

  const tabCount = useCallback(
    (key: string) => {
      const bookingList = diaryContext.bookingsFiltered;

      switch (key) {
        case BookingTypeEnum.BOOKINGS:
          return filterBookings(bookingList, completeBookingsFilter).length;

        case BookingTypeEnum.WAITLIST:
          return filterBookings(bookingList, waitlistBookingsFilter).length;

        case BookingTypeEnum.OPEN:
        case BookingTypeEnum.NEW:
          return filterBookings(bookingList, openBookingsFilter).length;

        case BookingTypeEnum.LEFT:
          return filterBookings(bookingList, leftBookingsFilter).length;
      }

      return 0;
    },
    [diaryContext.bookingsFiltered]
  );

  const unSyncedCount = useMemo(() => {
    return (
      diaryContext.bookingsFiltered?.filter(
        (booking: ViewBooking & editBooking) => !booking.id || booking.unsaved
      ).length ?? 0
    );
  }, [diaryContext.bookingsFiltered]);

  const handleBookingsClick = useCallback(() => {
    setShowAddBooking(false);
    setCurrentSectionKey("bookings");
  }, []);

  const handleWaitlistClick = useCallback(() => {
    setShowAddBooking(false);
    setCurrentSectionKey("waitlist");
  }, []);

  const handleOpenClick = useCallback(() => {
    setShowAddBooking(false);
    setCurrentSectionKey("open");
  }, []);

  const handleLeftClick = useCallback(() => {
    setShowAddBooking(false);
    setCurrentSectionKey("left");
  }, []);

  const handleUnsyncedClick = useCallback(() => {
    setShowAddBooking(false);
    setCurrentSectionKey("unsynced");
  }, []);

  const diarySections: DiarySection[] = useMemo(
    () => [
      {
        key: "bookings",
        icon: "bookings",
        title: "Bookings",
        count: tabCount(BookingTypeEnum.BOOKINGS),
        onClick: handleBookingsClick,
        showFilter: true,
        initialFilterQuery: completeBookingsFilter,
        isEnabled: true
      },
      {
        key: "waitlist",
        icon: "waitlist",
        title: "Waitlist",
        count: tabCount(BookingTypeEnum.WAITLIST),
        onClick: handleWaitlistClick,
        showFilter: true,
        initialFilterQuery: waitlistBookingsFilter,
        isEnabled: true
      },
      {
        key: "open",
        icon: "open",
        title: "Open",
        count: tabCount(BookingTypeEnum.OPEN),
        onClick: handleOpenClick,
        showFilter: true,
        initialFilterQuery: openBookingsFilter,
        isEnabled: true
      },
      {
        key: "left",
        icon: "left",
        title: "Left",
        count: tabCount(BookingTypeEnum.LEFT),
        onClick: handleLeftClick,
        showFilter: true,
        initialFilterQuery: leftBookingsFilter,
        isEnabled: true
      },
      {
        key: "unsynced",
        icon: "sync",
        title: "Unsynced",
        count: unSyncedCount,
        onClick: handleUnsyncedClick,
        showFilter: false,
        initialFilterQuery: (booking: ViewBooking & editBooking) =>
          !booking.id || booking.unsaved,
        isEnabled: unSyncedCount > 0
      }
    ],
    [
      tabCount,
      unSyncedCount,
      handleBookingsClick,
      handleWaitlistClick,
      handleOpenClick,
      handleLeftClick,
      handleUnsyncedClick
    ]
  );

  // Collapse button moved to SubHeaderImproved component

  if (showSidebar === undefined) {
    return null;
  }

  // Hide sidebar when viewing a booking opened from enquiries
  const shouldShowSidebar =
    showSidebar &&
    diaryContext.selectedView !== "RunsheetTab" &&
    !openedFromEnquiry;

  return (
    <ExpandableSidebar isExpanded={shouldShowSidebar}>
      {showAddBooking && (
        <div style={{ height: "100%" }}>
          <DiaryAddBookingSection
            prefillBookingType={prefillBookingType}
            setPrefillBookingType={setPrefillBookingType}
            setShowAddBooking={setShowAddBooking}
          />
        </div>
      )}

      {!showAddBooking && (
        <div className="DiarySearchRow flex flex-col w-full h-full relative overflow-visible">
          <DiarySearch
            onFocus={(result: boolean) => {
              setShowSearchForBooking(result);
            }}
          />

          <div
            className={[
              "BookingShortcuts__wrapper",
              "transition-[background-color,color] duration-300 ease-in-out",
              "overflow-hidden pb-2 pl-3",
              showSearchForBooking
                ? "max-h-0 p-0 m-0 opacity-0"
                : "max-h-[60px] min-h-9"
            ].join(" ")}
          >
            <BookingShortcuts onBookingShortcut={toggleAddBooking} />
          </div>

          <motion.div
            className="SideBarTabListContainer flex flex-col w-[var(--sidebarWidth)]"
            animate={
              !showAddBooking && !showSearchForBooking ? "active" : "inactive"
            }
            variants={{
              active: {
                display: "flex",
                height: "100%",
                transition: { duration: 0.25 }
              },
              inactive: {
                display: "none",
                transition: { duration: 0.05 }
              }
            }}
          >
            <DiaryTabsWrapper
              selectedSectionKey={
                showAddBooking || !showSidebar || showSearchForBooking
                  ? ""
                  : currentSectionKey
              }
              diarySections={diarySections}
            />
            <DiarySidebarSections
              sections={diarySections}
              bookings={diaryContext.bookingsFiltered}
              venueConfig={diaryContext.venueConfig}
              venueGroupConfig={appContext.venueGroupConfig}
              isInitialising={diaryContext.isInitialising}
              isViewTransitioning={diaryContext.isViewTransitioning}
              currentSectionKey={currentSectionKey}
            />
          </motion.div>
        </div>
      )}
    </ExpandableSidebar>
  );
};
